import { CdkMenuModule } from '@angular/cdk/menu';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SiAccordionModule } from '@siemens/element-ng/accordion';
import { ActionDialog, SiActionDialogService } from '@siemens/element-ng/action-modal';
import { Filter, SiFilterBarModule } from '@siemens/element-ng/filter-bar';
import { SiFormModule } from '@siemens/element-ng/form';
import { SiMenuModule } from '@siemens/element-ng/menu';
import { SiModalService } from '@siemens/element-ng/modal';
import { SiSearchBarModule } from '@siemens/element-ng/search-bar';
import { SelectItem, SiSelectModule } from '@siemens/element-ng/select';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { SiTooltipDirective } from '@siemens/element-ng/tooltip';
import { SiTreeViewModule, TreeItem, TreeItemFolderState } from '@siemens/element-ng/tree-view';
import { concat, Observable, switchMap, toArray } from 'rxjs';
import { STANDARD_MODAL_CONFIG } from 'src/app/main/shared/const';
import {
  ItemsToWorkspacesData,
  TagsToItemsData,
  AddTagToWorkspaceData,
  DeleteItemsFromWorkspaceData,
  Schedule,
  Tag,
  Track,
  Vehicle,
  Workspace
} from 'src/app/main/shared/DataTypes';
import { SettingsService } from 'src/app/main/shared/services/settings.service';
import { ShareDataService } from 'src/app/main/shared/services/share-data.service';
import { WorkspaceService } from 'src/app/main/shared/services/workspace.service';

import { TrackService } from '../track/track.service';

@Component({
  selector: 'app-load-modal',
  imports: [
    SiSearchBarModule,
    SiFormModule,
    FormsModule,
    TranslateModule,
    SiAccordionModule,
    SiTreeViewModule,
    SiFilterBarModule,
    CommonModule,
    SiSelectModule,
    SiMenuModule,
    CdkMenuModule,
    SiTooltipDirective
  ],
  templateUrl: './load-modal.component.html',
  styleUrl: './load-modal.component.scss'
})
export class LoadModalComponent {
  modalService = inject(SiModalService);
  siModal = inject(SiActionDialogService);
  workspaceService = inject(WorkspaceService);
  toastNotificationService = inject(SiToastNotificationService);
  translateService = inject(TranslateService);
  trackService = inject(TrackService);
  shareDataService = inject(ShareDataService);
  settingsService = inject(SettingsService);

  readonly openItemClicked = output<Track | Schedule | Vehicle>();
  readonly availableWorkspaces = this.shareDataService.availableWorkspaces;
  readonly dataType = input.required<'track' | 'schedule' | 'vehicle'>();

  readonly searchBarInput = signal<string>('');

  readonly viewMode = signal<'load' | 'edit'>('load');

  readonly showRemovedItems = signal<boolean>(false);

  readonly selectedWorkspaceId = signal<number | undefined>(undefined);
  readonly selectedWorkspace = computed(() => {
    const id = this.selectedWorkspaceId();
    if (id === undefined) return undefined;
    return this.availableWorkspaces().find(w => w.id === id);
  });

  private defaultWorkspaceApplied = false;

  constructor() {
    // Workspaces arrive asynchronously, so the preselection waits for them and runs only once
    effect(() => {
      const workspaces = this.availableWorkspaces();
      if (
        this.defaultWorkspaceApplied ||
        this.dataType() === 'vehicle' ||
        workspaces.length === 0
      ) {
        return;
      }
      this.defaultWorkspaceApplied = true;
      this.selectedWorkspaceId.set(this.settingsService.resolveDefaultWorkspace(workspaces)?.id);
    });
  }

  // Form data for saving items to workspace
  readonly selectedWorkspaceIdsToSaveTrackIn = signal<number[]>([]);
  readonly workspacesToSaveTrackInOptions = computed<SelectItem<string | number>[]>(() => {
    return this.availableWorkspaces().map((workspace: Workspace) => ({
      type: 'option',
      value: workspace.id,
      label: workspace.name
    }));
  });

  // Form data for deleting Tags from workspace
  readonly selectedTagIdsToDeleteFromWorkspace = signal<number[]>([]);
  readonly tagsToRemoveFromWorkspaceOptions = computed<SelectItem<number>[]>(() => {
    return this.selectedWorkspace()!.tags.map((tag: Tag) => ({
      type: 'option',
      value: tag.id,
      label: tag.name
    }));
  });

  // Form data for removing tags from items
  readonly selectedTagIdsToRemoveFromItems = signal<number[]>([]);
  readonly tagsToRemoveFromItemsOptions = computed<SelectItem<number>[]>(() => {
    return this.tagsFromSelectedItems().map((tag: Tag) => ({
      type: 'option',
      value: tag.id,
      label: tag.name
    }));
  });

  // Form data for adding tags to items
  readonly selectedTagIdsToAddToItems = signal<number[]>([]);
  readonly tagsToAddToItemsOptions = computed<SelectItem<number>[]>(() => {
    const selectedItems = this.checkedVariants();
    if (selectedItems.length === 0) return [];

    // Collect ALL tag IDs from ANY selected item
    const allSelectedTagIds = new Set(
      selectedItems.flatMap(item => item.tags?.map(tag => tag.id) || [])
    );

    // Filter workspace tags to exclude ANY tags that appear in ANY selected item
    return this.selectedWorkspace()!
      .tags.filter(tag => !allSelectedTagIds.has(tag.id))
      .map((tag: Tag) => ({
        type: 'option',
        value: tag.id,
        label: tag.name
      }));
  });

  // Form data for adding tag to workspace
  readonly tagNameToAddToWorkspace = signal<string>('');

  readonly tagsFromSelectedItems = computed(() => {
    const selectedItems = this.checkedVariants();
    if (selectedItems.length === 0) return [];

    // Get tags from first item
    const firstItemTags = selectedItems[0].tags || [];

    // Find common tags across all selected items
    return firstItemTags.filter(tag =>
      selectedItems.every(item => item.tags?.some(itemTag => itemTag.id === tag.id))
    );
  });

  // Determines if the selected items are deleted or not
  readonly areSelectedItemsDeleted = computed(() => {
    const selectedItems = this.checkedVariants();
    console.log('selectedItems', selectedItems);
    if (selectedItems.length === 0) return false;
    // Check if all selected items are deleted
    return selectedItems.every(item => item.deleted);
  });

  readonly treeItems = computed<TreeItem[]>(() => {
    // Vehicles are only maintained in the Public workspace, so they are listed without grouping
    if (this.dataType() === 'vehicle') {
      const publicWorkspace = this.publicWorkspace();
      return publicWorkspace ? this.getWorkspaceChildren(publicWorkspace) : [];
    }
    // If a workspace is selected, only show its children (groups/variants)
    if (this.selectedWorkspace()) {
      return this.getWorkspaceChildren(this.selectedWorkspace()!);
    }
    // Otherwise show all workspaces and their items
    return this.availableWorkspaces().map(workspace => this.createWorkspaceTreeItem(workspace));
  });

  readonly publicWorkspace = computed(() =>
    this.availableWorkspaces().find(
      workspace => workspace.name === 'Public' && !workspace.isPrivate
    )
  );

  private showsWorkspaceRoots(): boolean {
    return !this.selectedWorkspace() && this.dataType() !== 'vehicle';
  }

  readonly filteredTreeItems = computed<TreeItem[]>(() => {
    let filteredItems = this.treeItems();

    if (this.dataType() === 'vehicle') {
      filteredItems = filteredItems.filter(
        (vehicle: TreeItem) => !vehicle.customData.vehicle.deleted
      );
    }

    // Filter out all items marked as "removed" if the checkbox is not checked
    if (!this.showRemovedItems()) {
      if (this.dataType() === 'track' || this.dataType() === 'schedule') {
        if (this.selectedWorkspace()) {
          // We're in workspace view, items start at group level
          filteredItems = this.filterGroupsAndVariantsForDeletedItems(filteredItems);
        } else {
          // We're in "All" view, need to filter through workspaces
          filteredItems = filteredItems
            .map(workspace => ({
              ...workspace,
              children: this.filterGroupsAndVariantsForDeletedItems(workspace.children!)
            }))
            .filter(workspace => workspace.children!.length > 0);
        }
      }
    }

    // Filter out empty workspaces if view is on "All"
    if (this.showsWorkspaceRoots()) {
      filteredItems = filteredItems.filter((rootItem: TreeItem) => {
        if (this.dataType() === 'vehicle') {
          return rootItem.customData.workspace.vehicles?.length > 0;
        } else if (this.dataType() === 'track') {
          return rootItem.customData.workspace.tracks?.length > 0;
        } else if (this.dataType() === 'schedule') {
          return rootItem.customData.workspace.schedules?.length > 0;
        }
        return true;
      });
    }

    // filter by tags if any are selected and a workspace is selected
    if (this.tagNamesToFilterFor().length > 0 && this.selectedWorkspace()) {
      if (this.dataType() === 'vehicle') {
        filteredItems = filteredItems.filter((vehicle: TreeItem) =>
          vehicle.customData.vehicle.tags?.some((tag: Tag) =>
            this.tagNamesToFilterFor().includes(tag.name)
          )
        );
      } else {
        filteredItems = filteredItems.reduce((filteredGroups: TreeItem[], group: TreeItem) => {
          const filteredVariants = group.children?.filter((variant: TreeItem) =>
            variant.customData.variant.tags?.some((tag: Tag) =>
              this.tagNamesToFilterFor().includes(tag.name)
            )
          );

          if (filteredVariants && filteredVariants.length > 0) {
            filteredGroups.push({
              ...group,
              children: filteredVariants
            });
          }

          return filteredGroups;
        }, []);
      }
    }

    // Then filter by search text if any
    const searchText = this.searchBarInput().toLowerCase().trim();
    if (searchText) {
      if (this.showsWorkspaceRoots()) {
        // No workspace selected, filter through workspaces, groups, and variants/vehicles
        filteredItems = filteredItems.reduce(
          (filteredWorkspaces: TreeItem[], workspace: TreeItem) => {
            // Check workspace name
            if (workspace.customData.workspace.name.toLowerCase().includes(searchText)) {
              filteredWorkspaces.push(workspace);
              return filteredWorkspaces;
            }

            if (this.dataType() === 'vehicle') {
              // Filter vehicles within workspace
              const filteredVehicles = workspace.children?.filter((vehicle: TreeItem) =>
                vehicle.customData.name.toLowerCase().includes(searchText)
              );

              if (filteredVehicles && filteredVehicles.length > 0) {
                filteredWorkspaces.push({
                  ...workspace,
                  children: filteredVehicles
                });
              }
            } else {
              // Filter groups and variants within workspace
              const filteredGroups = workspace.children?.reduce(
                (groups: TreeItem[], group: TreeItem) => {
                  // Check group name
                  if (group.customData.name.toLowerCase().includes(searchText)) {
                    groups.push(group);
                    return groups;
                  }

                  // Check variants within group
                  const filteredVariants = group.children?.filter((variant: TreeItem) =>
                    variant.customData.variant.version.toLowerCase().includes(searchText)
                  );

                  if (filteredVariants && filteredVariants.length > 0) {
                    groups.push({
                      ...group,
                      children: filteredVariants
                    });
                  }

                  return groups;
                },
                []
              );

              if (filteredGroups && filteredGroups.length > 0) {
                filteredWorkspaces.push({
                  ...workspace,
                  children: filteredGroups
                });
              }
            }

            return filteredWorkspaces;
          },
          []
        );
      } else {
        // Workspace selected, only filter groups and variants/vehicles
        if (this.dataType() === 'vehicle') {
          // Filter vehicles directly
          filteredItems = filteredItems.filter((vehicle: TreeItem) =>
            vehicle.customData.name.toLowerCase().includes(searchText)
          );
        } else {
          // Filter groups and variants
          filteredItems = filteredItems.reduce((filteredGroups: TreeItem[], group: TreeItem) => {
            // Check group name
            if (group.customData.name.toLowerCase().includes(searchText)) {
              filteredGroups.push(group);
              return filteredGroups;
            }

            // Check variants within group
            const filteredVariants = group.children?.filter((variant: TreeItem) =>
              variant.customData.variant.version.toLowerCase().includes(searchText)
            );

            if (filteredVariants && filteredVariants.length > 0) {
              filteredGroups.push({
                ...group,
                children: filteredVariants
              });
            }

            return filteredGroups;
          }, []);
        }
      }
    }

    return filteredItems;
  });

  private filterGroupsAndVariantsForDeletedItems(groups: TreeItem[]): TreeItem[] {
    return groups
      .map(group => ({
        ...group,
        children: group.children!.filter(variant => !variant.customData.variant.deleted)
      }))
      .filter(group => group.children!.length > 0);
  }

  // Tree View configuration
  readonly showCheckboxes = computed(() => {
    return this.viewMode() === 'edit' && this.selectedWorkspace();
  });
  readonly enableSelection = computed(() => {
    return this.viewMode() === 'load';
  });

  readonly selectedTreeItem = signal<TreeItem | undefined>(undefined);

  readonly selectedTreeItemType = computed<'workspace' | 'variant' | 'vehicle' | undefined>(() => {
    if (this.selectedTreeItem()?.templateName) {
      return this.selectedTreeItem()?.templateName as 'workspace' | 'variant' | 'vehicle';
    }
    return undefined;
  });

  toggleViewMode() {
    this.viewMode.update(currentMode => (currentMode === 'load' ? 'edit' : 'load'));
  }

  onOpenItemClick() {
    if (this.selectedTreeItemType() == 'variant') {
      const selectedItem = this.selectedTreeItem()?.customData.variant;
      this.openItemClicked.emit(selectedItem);
    }
    if (this.selectedTreeItemType() == 'vehicle') {
      const selectedItem = this.selectedTreeItem()?.customData.vehicle;
      this.openItemClicked.emit(selectedItem);
    }
  }

  onAddItemsToWorkspacesClick() {
    const addItemsToWorkspacesData: ItemsToWorkspacesData = {
      workspaceIds: this.selectedWorkspaceIdsToSaveTrackIn(),
      itemIds: this.checkedVariants().map((variant: Track | Schedule) => variant.id!),
      dataType: this.dataType()
    };
    this.workspaceService.addItemsToWorkspaces(addItemsToWorkspacesData).subscribe({
      error: e => {
        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: this.translateService.instant('SHARED.ERROR_SAVE', {
            type: this.translateService.instant(
              this.dataType().toUpperCase() + '.' + this.dataType().toUpperCase()
            )
          }),
          message: e.statusText
        });
      },
      next: (response: any) => {
        // Update available workspaces from DB
        this.workspaceService.getAllWorkspaces().subscribe({
          next: (workspaces: Workspace[]) => {
            this.shareDataService.setAvailableWorkspaces(workspaces);
          }
        });
      },
      complete: () => {
        this.toastNotificationService.showToastNotification({
          state: 'success',
          title: this.translateService.instant('SHARED.SUCCESS'),
          message: this.translateService.instant('SHARED.SUCCESS_ADD', {
            type: this.translateService.instant(
              this.dataType().toUpperCase() + '.' + this.dataType().toUpperCase() + 'S'
            )
          })
        });
      }
    });
  }

  onAddTagsToItemsClick() {
    const addTagsToItemsData: TagsToItemsData = {
      tagIds: this.selectedTagIdsToAddToItems(),
      itemIds: this.checkedVariants().map((variant: Track | Schedule) => variant.id!),
      dataType: this.dataType()
    };
    this.workspaceService.addTagsToItems(addTagsToItemsData).subscribe({
      error: e => {
        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: this.translateService.instant('SHARED.ERROR'),
          message: e.statusText
        });
      },
      next: (response: any) => {
        // Update available workspaces from DB
        this.workspaceService.getAllWorkspaces().subscribe({
          next: (workspaces: Workspace[]) => {
            this.shareDataService.setAvailableWorkspaces(workspaces);
          }
        });
      },
      complete: () => {
        this.toastNotificationService.showToastNotification({
          state: 'success',
          title: this.translateService.instant('SHARED.SUCCESS'),
          message: this.translateService.instant('SHARED.SUCCESS_ADD', {
            type: this.translateService.instant('SHARED.TAGS')
          })
        });
      }
    });
  }

  onRemoveTagsFromItemsClick() {
    const removeTagsToItemsData: TagsToItemsData = {
      tagIds: this.selectedTagIdsToRemoveFromItems(),
      itemIds: this.checkedVariants().map((variant: Track | Schedule) => variant.id!),
      dataType: this.dataType()
    };
    this.workspaceService.removeTagsFromItems(removeTagsToItemsData).subscribe({
      error: e => {
        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: this.translateService.instant('SHARED.ERROR'),
          message: e.statusText
        });
      },
      next: (response: any) => {
        // Update available workspaces from DB
        this.workspaceService.getAllWorkspaces().subscribe({
          next: (workspaces: Workspace[]) => {
            this.shareDataService.setAvailableWorkspaces(workspaces);
          }
        });
      },
      complete: () => {
        this.toastNotificationService.showToastNotification({
          state: 'success',
          title: this.translateService.instant('SHARED.SUCCESS'),
          message: this.translateService.instant('SHARED.SUCCESS_REMOVE', {
            type: this.translateService.instant('SHARED.TAGS')
          })
        });
      }
    });
  }

  onAddTagToWorkspaceClick() {
    const addTagToWorkspaceData: AddTagToWorkspaceData = {
      tagName: this.tagNameToAddToWorkspace(),
      workspaceId: this.selectedWorkspace()!.id
    };
    this.tagNameToAddToWorkspace.set('');
    this.workspaceService.addTagToWorkspace(addTagToWorkspaceData).subscribe({
      error: e => {
        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: this.translateService.instant('SHARED.ERROR'),
          message: e.statusText
        });
      },
      next: (response: any) => {
        // Update available workspaces from DB
        this.workspaceService.getAllWorkspaces().subscribe({
          next: (workspaces: Workspace[]) => {
            this.shareDataService.setAvailableWorkspaces(workspaces);
          }
        });
      },
      complete: () => {
        this.toastNotificationService.showToastNotification({
          state: 'success',
          title: this.translateService.instant('SHARED.SUCCESS'),
          message: this.translateService.instant('SHARED.SUCCESS_ADD', {
            type: this.translateService.instant('SHARED.TAGS')
          })
        });
      }
    });
  }

  readonly checkedWorkspaces = signal<Workspace[]>([]);
  readonly checkedVariants = signal<Track[] | Schedule[]>([]);
  // Gets called when an action buttoon is clicked or an item gets checked/unchecked
  // TODO: Avoid checking everytime an item is checked/unchecked
  updateCheckedElements() {
    const checkedWorkspaces: Workspace[] = [];
    const checkedVariants: Track[] | Schedule[] = [];

    if (this.selectedWorkspace()) {
      // If a workspace is selected, check its children directly
      this.treeItems().forEach((group: any) => {
        group.children?.forEach((variant: any) => {
          if (variant.checked === 'checked') {
            checkedVariants.push(variant.customData.variant);
          }
        });
      });
    } else {
      // Otherwise check through all workspaces
      this.treeItems().forEach((workspace: any) => {
        if (workspace.checked === 'checked') {
          checkedWorkspaces.push(workspace.customData.workspace);
        }
        workspace.children?.forEach((group: any) => {
          group.children?.forEach((variant: any) => {
            if (variant.checked === 'checked') {
              checkedVariants.push(variant.customData.variant);
            }
          });
        });
      });
    }

    this.checkedWorkspaces.update(() => checkedWorkspaces);
    this.checkedVariants.update(() => checkedVariants);
  }

  // Tags
  readonly visibleTagBadges = signal<Filter[]>([]);
  readonly tagNamesToFilterFor = computed(() => {
    return this.visibleTagBadges().map(tag => tag.filterName);
  });

  onTagSelect(selectedTagName: string): void {
    // Check if the tag is already in visibleTagBadges
    const tagInVisibleTags = this.visibleTagBadges().find(
      tag => tag.filterName === selectedTagName
    );

    if (!tagInVisibleTags) {
      const filter: Filter = {
        filterName: selectedTagName,
        title: selectedTagName,
        status: 'default'
      };
      this.visibleTagBadges.update(badges => [...badges, filter]);
    }
  }

  openModal(template: TemplateRef<any>): void {
    this.modalService.show(template, STANDARD_MODAL_CONFIG);
  }

  closeModal(modalRef: any): void {
    modalRef.hide();
  }

  onSelectedWorkspaceChange() {
    // Reset selected tags
    this.visibleTagBadges.update(() => []);
    if (!this.selectedWorkspace()) {
      this.viewMode.set('load');
    }
  }

  private createWorkspaceTreeItem(workspace: Workspace): TreeItem {
    return {
      templateName: 'workspace',
      customData: {
        workspace: workspace
      },
      children: this.getWorkspaceChildren(workspace)
    };
  }

  private getWorkspaceChildren(workspace: Workspace): TreeItem[] {
    switch (this.dataType()) {
      case 'track':
        return (
          workspace.tracks?.map(item => ({
            templateName: 'group',
            customData: {
              name: item.name
            },
            children: item.variants?.map(variant => ({
              templateName: 'variant',
              state: 'leaf' as TreeItemFolderState,
              customData: {
                variant: variant
              }
            }))
          })) ?? []
        );

      case 'schedule':
        return (
          workspace.schedules?.map(item => ({
            templateName: 'group',
            customData: {
              name: item.name
            },
            children: item.variants?.map(variant => ({
              templateName: 'variant',
              state: 'leaf' as TreeItemFolderState,
              customData: {
                variant: variant
              }
            }))
          })) ?? []
        );

      case 'vehicle':
        return (
          workspace.vehicles?.map(vehicle => ({
            templateName: 'vehicle',
            state: 'leaf' as TreeItemFolderState,
            customData: {
              name: vehicle.name,
              type: vehicle.type,
              vehicle: vehicle
            }
          })) ?? []
        );

      default:
        return [];
    }
  }

  showDeleteTagsActionDialog(dialog: ActionDialog, modalRef: any): void {
    this.siModal.showActionDialog(dialog).subscribe(result => {
      // Check if the user confirmed the action
      if (result === 'delete') {
        const deleteTagsFromWorkspaceData: DeleteItemsFromWorkspaceData = {
          itemIds: this.selectedTagIdsToDeleteFromWorkspace(),
          workspaceId: this.selectedWorkspace()!.id
        };

        // Cleanup
        this.selectedTagIdsToDeleteFromWorkspace.set([]);
        this.closeModal(modalRef);

        // Delete tags from workspace
        this.workspaceService.deleteTagsFromWorkspace(deleteTagsFromWorkspaceData).subscribe({
          error: e => {
            this.toastNotificationService.showToastNotification({
              state: 'danger',
              title: this.translateService.instant('SHARED.ERROR'),
              message: e.statusText
            });
          },
          next: (response: any) => {
            // Update available workspaces from DB
            this.workspaceService.getAllWorkspaces().subscribe({
              next: (workspaces: Workspace[]) => {
                this.shareDataService.setAvailableWorkspaces(workspaces);
              }
            });
          },
          complete: () => {
            this.toastNotificationService.showToastNotification({
              state: 'success',
              title: this.translateService.instant('SHARED.SUCCESS'),
              message: this.translateService.instant('SHARED.SUCCESS_DELETE', {
                type: this.translateService.instant('SHARED.TAGS')
              })
            });
          }
        });
      }
    });
  }

  showRemoveItemsFromWorkspaceDialog(): void {
    const dialog: ActionDialog = {
      type: 'delete-confirm',
      heading: this.translateService.instant('SHARED.REMOVE_ITEM', {
        type: this.translateService.instant(
          this.dataType().toUpperCase() + '.' + this.dataType().toUpperCase()
        )
      }),
      message: this.translateService.instant('SHARED.INFO_REMOVE', {
        type: this.translateService.instant(
          this.dataType().toUpperCase() + '.' + this.dataType().toUpperCase() + 'S'
        )
      })
    };
    this.siModal.showActionDialog(dialog).subscribe(result => {
      // Check if the user confirmed the action
      if (result === 'delete') {
        const deleteTagsFromWorkspaceData: ItemsToWorkspacesData = {
          itemIds: this.checkedVariants().map((variant: Track | Schedule) => variant.id!),
          workspaceIds: [this.selectedWorkspace()!.id],
          dataType: this.dataType()
        };

        // Delete items from workspace
        this.workspaceService.removeItemsFromWorkspaces(deleteTagsFromWorkspaceData).subscribe({
          error: e => {
            this.toastNotificationService.showToastNotification({
              state: 'danger',
              title: this.translateService.instant('SHARED.ERROR'),
              message: e.statusText
            });
          },
          next: (response: any) => {
            // Update available workspaces from DB
            this.workspaceService.getAllWorkspaces().subscribe({
              next: (workspaces: Workspace[]) => {
                this.shareDataService.setAvailableWorkspaces(workspaces);
              }
            });
          },
          complete: () => {
            this.toastNotificationService.showToastNotification({
              state: 'success',
              title: this.translateService.instant('SHARED.SUCCESS'),
              message: this.translateService.instant('SHARED.SUCCESS_REMOVE', {
                type: this.translateService.instant(
                  this.dataType().toUpperCase() + '.' + this.dataType().toUpperCase() + 'S'
                )
              })
            });
          }
        });
      }
    });
  }

  showDeleteItemsDialog(): void {
    this.siModal
      .showActionDialog({
        type: 'delete-confirm',
        heading: this.translateService.instant('SHARED.DELETE_ITEM', {
          type: this.translateService.instant(
            this.dataType().toUpperCase() + '.' + this.dataType().toUpperCase()
          )
        }),
        message: this.translateService.instant('SHARED.INFO_DELETE', {
          type: this.translateService.instant(
            this.dataType().toUpperCase() + '.' + this.dataType().toUpperCase() + 'S'
          )
        })
      })
      .subscribe(result => {
        if (result !== 'delete') return;

        const selectedItemIds = this.checkedVariants().map(
          (variant: Track | Schedule) => variant.id!
        );

        const deleteItems$ = (
          itemIdsToDelete: number[],
          dataType: ItemsToWorkspacesData['dataType']
        ): Observable<unknown> =>
          this.workspaceService.deleteItems({
            itemIds: itemIdsToDelete,
            workspaceIds: [this.selectedWorkspace()!.id],
            dataType
          });

        // Runs the deletions in order and reloads the workspaces only once afterwards
        const performDeletion = (deleteRequests: Observable<unknown>[]) => {
          concat(...deleteRequests)
            .pipe(
              toArray(),
              switchMap(() => this.workspaceService.getAllWorkspaces())
            )
            .subscribe({
              error: e => {
                this.toastNotificationService.showToastNotification({
                  state: 'danger',
                  title: this.translateService.instant('SHARED.ERROR'),
                  message: e.statusText
                });
              },
              next: (workspaces: Workspace[]) => {
                this.shareDataService.setAvailableWorkspaces(workspaces);
              },
              complete: () => {
                this.toastNotificationService.showToastNotification({
                  state: 'success',
                  title: this.translateService.instant('SHARED.SUCCESS'),
                  message: this.translateService.instant('SHARED.SUCCESS_DELETE', {
                    type: this.translateService.instant(
                      this.dataType().toUpperCase() + '.' + this.dataType().toUpperCase() + 'S'
                    )
                  })
                });
                this.viewMode.set('load');
              }
            });
        };

        // If not track, delete immediately
        if (this.dataType() !== 'track') {
          performDeletion([deleteItems$(selectedItemIds, this.dataType())]);
          return;
        }

        // For tracks, check first if there are any schedules containing the selected tracks
        this.trackService
          .getAllSchedulesContainingTracks(selectedItemIds)
          .subscribe((response: any) => {
            if (response.length === 0) {
              performDeletion([deleteItems$(selectedItemIds, 'track')]);
              return;
            }

            // Show warning and wait for second confirmation
            const scheduleNamesContainingTracks = response
              .map((schedule: any) => schedule.name)
              .join(', ');
            this.siModal
              .showActionDialog({
                type: 'delete-confirm',
                heading: this.translateService.instant('SHARED.WARNING'),
                message: this.translateService.instant(
                  'LOAD_MODAL.INFO_SCHEDULES_CONTAINING_TRACKS',
                  {
                    schedules: scheduleNamesContainingTracks
                  }
                )
              })
              .subscribe(secondResult => {
                if (secondResult === 'delete') {
                  const scheduleIdsToDelete = response.map((schedule: any) => schedule.id);
                  performDeletion([
                    deleteItems$(scheduleIdsToDelete, 'schedule'),
                    deleteItems$(selectedItemIds, 'track')
                  ]);
                }
              });
          });
      });
  }

  showRestoreItemsDialog(): void {
    const dialog: ActionDialog = {
      type: 'confirmation',
      heading: this.translateService.instant('SHARED.RESTORE', {
        type: this.translateService.instant(
          this.dataType().toUpperCase() + '.' + this.dataType().toUpperCase()
        )
      }),
      message: this.translateService.instant('SHARED.INFO_RESTORE', {
        type: this.translateService.instant(
          this.dataType().toUpperCase() + '.' + this.dataType().toUpperCase() + 'S'
        )
      })
    };
    this.siModal.showActionDialog(dialog).subscribe(result => {
      // Check if the user confirmed the action
      if (result === 'confirm') {
        const restoreItemsData: ItemsToWorkspacesData = {
          itemIds: this.checkedVariants().map((variant: Track | Schedule) => variant.id!),
          workspaceIds: [this.selectedWorkspace()!.id],
          dataType: this.dataType()
        };

        // Delete items from workspace
        this.workspaceService.restoreItems(restoreItemsData).subscribe({
          error: e => {
            this.toastNotificationService.showToastNotification({
              state: 'danger',
              title: this.translateService.instant('SHARED.ERROR'),
              message: e.statusText
            });
          },
          next: (response: any) => {
            // Update available workspaces from DB
            this.workspaceService.getAllWorkspaces().subscribe({
              next: (workspaces: Workspace[]) => {
                this.shareDataService.setAvailableWorkspaces(workspaces);
              }
            });
          },
          complete: () => {
            this.toastNotificationService.showToastNotification({
              state: 'success',
              title: this.translateService.instant('SHARED.SUCCESS'),
              message: this.translateService.instant('SHARED.SUCCESS_RESTORE', {
                type: this.translateService.instant(
                  this.dataType().toUpperCase() + '.' + this.dataType().toUpperCase() + 'S'
                )
              })
            });
          }
        });
      }
    });
  }
}
